import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { CategoryEntity } from '@actual-app/core/types/models';

import type { ApplyBudgetActionPayload } from '#budget';
import {
  useBudgetActions,
  useCreateCategoryGroupMutation,
  useCreateCategoryMutation,
} from '#budget';
import { AyudaGavi } from '#components/mobile/ayuda/AyudaGavi';
import { esperar } from '#components/mobile/ayuda/escenas';
import { OjoBuey } from '#components/mobile/ayuda/OjoBuey';
import { usePreguntasAhorros } from '#components/mobile/ayuda/usePreguntasAhorros';
import type { PonerAhorros } from '#components/mobile/ayuda/usePreguntasAhorros';
import {
  numerarCumplidas,
  totalesDe,
} from '#components/mobile/bitacora/bitacora';
import type { Cumplida } from '#components/mobile/bitacora/bitacora';
import { HojaNuevaCategoria } from '#components/mobile/budget/HojaNuevaCategoria';
import {
  aspectoBotella,
  aspectoDeRecuerdo,
} from '#components/mobile/budget/recuerdosAspecto';
import { Botella } from '#components/mobile/budget/travesia/Botella';
import {
  RUTA_BITACORA,
  useLlegadas,
} from '#components/mobile/budget/travesia/ProveedorLlegadas';
import { porApartar } from '#components/mobile/budget/travesia/ritmo';
import { useRefrescarObjetivos } from '#components/mobile/budget/useDatosObjetivos';
import { rutaFicha } from '#components/mobile/budget/useFichaCategoria';
import { usePagadas } from '#components/mobile/budget/usePagadas';
import { useRecuerdos } from '#components/mobile/budget/useRecuerdos';
import type { EstadoTravesiaCategoria } from '#components/mobile/budget/useRecuerdos';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Gavi } from '#components/mobile/travesia/Gavi';
import { BarraProgreso } from '#components/mobile/ui/BarraProgreso';
import { Boton } from '#components/mobile/ui/Boton';
import { BotonRedondo } from '#components/mobile/ui/Cabecera';
import { Cargando } from '#components/mobile/ui/Cargando';
import { EstadoVacio } from '#components/mobile/ui/EstadoVacio';
import { Icono } from '#components/mobile/ui/Icono';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import {
  color,
  espacio,
  num,
  radio,
  TACTIL,
  texto,
} from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { useSpreadsheet } from '#hooks/useSpreadsheet';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { useUndo } from '#hooks/useUndo';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import { ahorrosSinDestino, conDescartada, leerDescartadas } from './ahorros';
import { EjemploAyuda } from './EjemploAyuda';
import type { EjemploAhorros } from './EjemploAyuda';

/** Ruta de «Ahorros» (brújula del Plan). */
export const RUTA_AHORROS = '/ahorros';

/** Madera de la repisa de las cumplidas (la de la bitácora). */
const MADERA = { fondo: '#3b2f25', repisa: '#2b221b', canto: '#6b5440' };

type Pestana = 'navegando' | 'cumplidas';

/**
 * «Ahorros» (Vadik, 26-oct-2026): desde la brújula del Plan, como «Pagos
 * fijos». Navegando: las travesías con lo que llevas y lo que toca apartar
 * este mes («Asignar»), Gavi señala lo que guarda dinero sin destino y
 * «Nueva travesía» crea la categoría en el grupo que elijas. Cumplidas: la
 * repisa, el total y el camino a la bitácora. El Plan no cambia.
 */
export function AhorrosPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pestana, setPestana] = useState<Pestana>('navegando');
  const { data: { grouped } = { list: [], grouped: [] } } = useCategories();
  // Ayuda de Gavi: cambia de pestaña y abre hojas de ejemplo; al salir,
  // todo vuelve a como estaba al abrirla.
  const [ayuda, setAyuda] = useState(false);
  const [ejemplo, setEjemplo] = useState<EjemploAhorros | null>(null);
  const pestanaAlAbrir = useRef<Pestana>('navegando');
  const poner: PonerAhorros = async (p, e, listo) => {
    setPestana(p);
    setEjemplo(e);
    await esperar(() => !listo || !!document.querySelector(listo), 2500);
    // Un fotograma más: lo que se enseña ya está pintado y en su sitio.
    await new Promise(r => window.requestAnimationFrame(() => r(undefined)));
  };
  const preguntas = usePreguntasAhorros({ pestana, poner });

  return (
    <Page
      padding={0}
      header={
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: espacio.fila,
            padding: `6px ${espacio.margen}px`,
            minHeight: 56,
          }}
        >
          <BotonRedondo
            icono="cl"
            aria-label={t('Back')}
            onPress={() => void navigate(-1)}
          />
          <View style={{ flex: 1, minWidth: 0 }}>
            <View data-ayuda="titulo-ahorros">
              <Text
                role="heading"
                aria-level={1}
                style={{
                  ...texto.titulo,
                  color: color.fg,
                  whiteSpace: 'nowrap',
                }}
              >
                <Trans>Savings goals</Trans>
              </Text>
              <Text style={{ fontSize: 13, fontWeight: 700, color: color.fg3 }}>
                <Trans>Money you set aside for something you choose</Trans>
              </Text>
            </View>
          </View>
          <OjoBuey
            onPress={() => {
              pestanaAlAbrir.current = pestana;
              setAyuda(true);
            }}
          />
        </View>
      }
    >
      <View
        data-testid="ahorros"
        style={{
          gap: espacio.tarjetas,
          paddingBottom: MOBILE_NAV_HEIGHT + espacio.margen,
          flexShrink: 0,
        }}
      >
        <View style={{ padding: `0 ${espacio.margen}px` }}>
          <Segmentado<Pestana>
            etiqueta={t('Savings goals')}
            opciones={[
              { valor: 'navegando', texto: t('At sea') },
              { valor: 'cumplidas', texto: t('Arrived') },
            ]}
            valor={pestana}
            onChange={setPestana}
            testId="ahorros-pestana"
            estirar
            alto={TACTIL}
          />
        </View>
        {pestana === 'navegando' ? <Navegando /> : <Cumplidas />}
      </View>
      {ejemplo && (
        <EjemploAyuda
          ejemplo={ejemplo}
          grupos={(grouped ?? []).filter(g => !g.is_income && !g.hidden)}
          onCerrar={() => setEjemplo(null)}
        />
      )}
      {ayuda && (
        <AyudaGavi
          pantalla={t('Savings goals')}
          preguntas={preguntas}
          alSalir={() => {
            setEjemplo(null);
            setPestana(pestanaAlAbrir.current);
          }}
          onCerrar={() => setAyuda(false)}
        />
      )}
    </Page>
  );
}

function Navegando() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mes = monthUtils.currentMonth();
  const { data: { grouped } = { list: [], grouped: [] } } = useCategories();
  const { travesias } = useLlegadas();
  const { fijos } = usePagadas(mes);
  const [rawDescartadas, setDescartadas] = useSyncedPref('ahorros-descartadas');
  const createCategory = useCreateCategoryMutation();
  const createCategoryGroup = useCreateCategoryGroupMutation();
  const [creando, setCreando] = useState(false);
  useRefrescarObjetivos(mes);

  const categorias = new Map(
    (grouped ?? []).flatMap(g => g.categories ?? []).map(c => [c.id, c]),
  );
  const lista = [...travesias.values()]
    .filter(v => categorias.has(v.categoriaId))
    .sort((a, b) => a.objetivo.fecha.localeCompare(b.objetivo.fecha));
  const celdas = useCeldasMes(
    lista.map(v => v.categoriaId),
    mes,
  );
  const sinDestino = ahorrosSinDestino({
    grupos: grouped ?? [],
    travesias: new Set(travesias.keys()),
    fijos,
    descartadas: leerDescartadas(rawDescartadas),
  });

  const zarpar = (categoryId: string) =>
    dispatch(
      pushModal({
        modal: {
          name: 'zarpar',
          options: { categoryId, month: mes, zarpe: true },
        },
      }),
    );

  let cuotas = 0;
  let apartado = 0;
  for (const v of lista) {
    const c = celdas[v.categoriaId];
    if (c) {
      cuotas += c.cuota;
      apartado += Math.min(c.asignado, c.cuota);
    }
  }

  if (!grouped) {
    return <Cargando />;
  }

  return (
    <>
      <SinDestino
        categorias={sinDestino}
        onDestino={c => zarpar(c.id)}
        onNo={c => setDescartadas(conDescartada(rawDescartadas, c.id))}
      />
      {lista.length === 0 ? (
        <View style={{ padding: `0 ${espacio.margen}px` }}>
          <EstadoVacio
            ilustracion="barquito"
            titulo={t('No voyages yet')}
            texto={t(
              'A voyage is money you set aside for something you choose, with an amount and a date: holidays, a new car, a cushion for surprises.',
            )}
            data-testid="ahorros-vacio"
          />
        </View>
      ) : (
        <>
          <Resumen apartado={apartado} cuotas={cuotas} />
          <Tarjeta
            relleno={0}
            style={{ margin: `0 ${espacio.margen}px` }}
            data-testid="ahorros-lista"
          >
            {lista.map((v, i) => {
              const categoria = categorias.get(v.categoriaId);
              return categoria ? (
                <FilaTravesia
                  key={v.categoriaId}
                  travesia={v}
                  categoria={categoria}
                  celda={celdas[v.categoriaId] ?? null}
                  mes={mes}
                  primera={i === 0}
                />
              ) : null;
            })}
          </Tarjeta>
        </>
      )}
      <View style={{ padding: `0 ${espacio.margen}px` }}>
        <Boton
          variante="fantasma"
          bloque
          data-testid="ahorros-nueva"
          onPress={() => setCreando(true)}
        >
          <Icono nombre="plus" size={18} />
          <Trans>New voyage</Trans>
        </Boton>
      </View>
      {creando && (
        <HojaNuevaCategoria
          modo="categoria"
          titulo={t('New voyage')}
          textoBoton={t('Continue')}
          grupos={(grouped ?? []).filter(g => !g.is_income && !g.hidden)}
          onCrear={(nombre, grupo) => {
            setCreando(false);
            if (!grupo) {
              return;
            }
            // La categoría nace en el Plan, en su grupo; luego, la hoja de
            // zarpar de siempre (importe, fecha y carta).
            void createCategory
              .mutateAsync({
                name: nombre,
                groupId: grupo.id,
                isIncome: false,
                isHidden: false,
              })
              .then(id => {
                if (id) {
                  zarpar(id);
                }
              });
          }}
          onCrearGrupo={async nombre => {
            try {
              const id = await createCategoryGroup.mutateAsync({
                name: nombre,
              });
              return id ?? null;
            } catch {
              return null;
            }
          }}
          onCerrar={() => setCreando(false)}
        />
      )}
    </>
  );
}

type Celda = { cuota: IntegerAmount; asignado: IntegerAmount };

/**
 * Cuota (`goal-<id>`, la que pide la meta) y asignado (`budget-<id>`) del mes
 * de cada categoría, en vivo. Sin leer aún: no está en el mapa.
 */
function useCeldasMes(
  ids: readonly string[],
  mes: string,
): Record<string, Celda> {
  const spreadsheet = useSpreadsheet();
  const [valores, setValores] = useState<Record<string, number>>({});
  const clave = [...ids].sort().join('\n');
  useEffect(() => {
    if (!clave) {
      return;
    }
    const hoja = monthUtils.sheetForMonth(mes);
    const unbinds: (() => void)[] = [];
    for (const id of clave.split('\n')) {
      for (const campo of ['goal', 'budget']) {
        const name = `${campo}-${id}`;
        unbinds.push(
          spreadsheet.bind(hoja, { name }, result => {
            const value = typeof result.value === 'number' ? result.value : 0;
            setValores(prev =>
              prev[name] === value ? prev : { ...prev, [name]: value },
            );
          }),
        );
      }
    }
    return () => unbinds.forEach(unbind => unbind());
  }, [clave, mes, spreadsheet]);

  const celdas: Record<string, Celda> = {};
  for (const id of ids) {
    const cuota = valores[`goal-${id}`];
    const asignado = valores[`budget-${id}`];
    if (cuota != null && asignado != null) {
      celdas[id] = { cuota, asignado };
    }
  }
  return celdas;
}

function Resumen({
  apartado,
  cuotas,
}: {
  apartado: IntegerAmount;
  cuotas: IntegerAmount;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  return (
    <Tarjeta
      style={{ margin: `0 ${espacio.margen}px`, gap: 10 }}
      data-testid="ahorros-resumen"
    >
      <Text style={{ ...texto.fila, ...num, fontWeight: 800, color: color.fg }}>
        {t('This month: set aside {{done}} of {{total}}', {
          done: format(apartado, 'financial'),
          total: format(cuotas, 'financial'),
        })}
      </Text>
      <BarraProgreso valor={cuotas > 0 ? apartado / cuotas : 0} />
    </Tarjeta>
  );
}

function FilaTravesia({
  travesia,
  categoria,
  celda,
  mes,
  primera,
}: {
  travesia: EstadoTravesiaCategoria;
  categoria: CategoryEntity;
  celda: Celda | null;
  mes: string;
  primera: boolean;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const applyBudgetAction = useBudgetActions();
  const { showUndoNotification } = useUndo();
  const hoy = monthUtils.currentDay();

  const meta = travesia.objetivo.importe;
  const saldo = travesia.saldo;
  const falta = celda ? porApartar(celda.cuota, celda.asignado) : 0;
  const aspecto = travesia.recuerdo
    ? aspectoDeRecuerdo(travesia.recuerdo, hoy)
    : aspectoBotella({
        clase: 'otra',
        importe: meta,
        zarpoEn: hoy,
        hasta: hoy,
        aportaciones: 0,
        largoCarta: 0,
        nombre: categoria.name,
      });

  const asignar = () => {
    if (!celda || falta <= 0) {
      return;
    }
    applyBudgetAction.mutate({
      month: mes,
      type: 'budget-amount',
      args: { category: categoria.id, amount: celda.asignado + falta },
    } as ApplyBudgetActionPayload);
    showUndoNotification({
      message: t('{{amount}} assigned to {{categoryName}} in {{month}}.', {
        amount: format(falta, 'financial'),
        categoryName: categoria.name,
        month: monthUtils.format(mes, 'MMMM', locale),
      }),
    });
  };

  return (
    <View
      data-testid="ahorros-travesia"
      data-categoria={categoria.id}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: `12px ${espacio.margen}px`,
        borderTop: primera ? undefined : `1px solid ${color.line}`,
      }}
    >
      <Button
        variant="bare"
        aria-label={categoria.name}
        onPress={() => void navigate(rutaFicha(categoria.id, mes))}
        style={{
          flex: 1,
          minWidth: 0,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          justifyContent: 'flex-start',
          textAlign: 'left',
          padding: 0,
        }}
      >
        <Botella aspecto={aspecto} ancho={26} style={{ flexShrink: 0 }} />
        <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
          <Text style={{ fontSize: 15, fontWeight: 700, color: color.fg }}>
            {categoria.name}
          </Text>
          <Text style={{ ...num, fontSize: 12.5, color: color.fg3 }}>
            {t('{{saved}} of {{goal}} · {{date}}', {
              saved: format(Math.max(0, saldo), 'financial'),
              goal: format(meta, 'financial'),
              date: monthUtils.format(
                travesia.objetivo.fecha,
                'd MMM yyyy',
                locale,
              ),
            })}
          </Text>
          <BarraProgreso
            valor={meta > 0 ? saldo / meta : 0}
            alto={5}
            style={{ marginTop: 3 }}
          />
        </View>
      </Button>
      <View style={{ alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
        {celda && celda.cuota > 0 && (
          <Text
            data-ayuda="ahorros-cuota"
            style={{
              ...num,
              fontSize: 12.5,
              fontWeight: 600,
              color: color.fg2,
              whiteSpace: 'nowrap',
            }}
          >
            {t('{{amount}} this month', {
              amount: format(celda.cuota, 'financial'),
            })}
          </Text>
        )}
        {celda &&
          celda.cuota > 0 &&
          (falta > 0 ? (
            <Boton
              variante="primario"
              data-testid="ahorros-asignar"
              onPress={asignar}
              style={{ minHeight: 34, padding: '0 12px', fontSize: 13 }}
            >
              {t('Assign {{amount}}', {
                amount: format(falta, 'financial'),
              })}
            </Boton>
          ) : (
            <Text
              data-testid="ahorros-apartado"
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: color.accent,
                whiteSpace: 'nowrap',
              }}
            >
              ✓ <Trans>Set aside</Trans>
            </Text>
          ))}
      </View>
    </View>
  );
}

/** Lo que guarda dinero sin destino: Gavi propone ponérselo (o «No»). */
function SinDestino({
  categorias,
  onDestino,
  onNo,
}: {
  categorias: readonly CategoryEntity[];
  onDestino: (c: CategoryEntity) => void;
  onNo: (c: CategoryEntity) => void;
}) {
  const { t } = useTranslation();
  if (categorias.length === 0) {
    return null;
  }
  return (
    <Tarjeta
      style={{ margin: `0 ${espacio.margen}px`, gap: 12 }}
      data-testid="ahorros-sin-destino"
    >
      <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
        <Gavi estado="ladea" ancho={44} style={{ flexShrink: 0 }} />
        <Text style={{ ...texto.cuerpo, flex: 1, color: color.fg }}>
          {categorias.length === 1
            ? t(
                '{{name}} keeps money but has no destination. Give it an amount and a date and you will know how much to set aside each month.',
                { name: categorias[0].name },
              )
            : t(
                'These categories keep money but have no destination. Give them an amount and a date and you will know how much to set aside each month.',
              )}
        </Text>
      </View>
      {categorias.slice(0, 4).map(c => (
        <View
          key={c.id}
          data-testid="ahorros-sin-destino-fila"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            borderTop: `1px solid ${color.line}`,
            paddingTop: 10,
          }}
        >
          <Text
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 15,
              fontWeight: 700,
              color: color.fg,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {c.name}
          </Text>
          <Boton
            variante="primario"
            onPress={() => onDestino(c)}
            style={{ minHeight: 36, padding: '0 12px', fontSize: 13 }}
          >
            <Trans>Give it a destination</Trans>
          </Boton>
          <Boton
            variante="fantasma"
            onPress={() => onNo(c)}
            style={{ minHeight: 36, padding: '0 12px', fontSize: 13 }}
          >
            <Trans>No</Trans>
          </Boton>
        </View>
      ))}
    </Tarjeta>
  );
}

function Cumplidas() {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const recuerdos = useRecuerdos();
  const hoy = monthUtils.currentDay();
  const lista = numerarCumplidas(recuerdos);
  const totales = totalesDe(lista);
  const abrirBitacora = (
    <Button
      variant="bare"
      data-testid="ahorros-bitacora"
      onPress={() => void navigate(RUTA_BITACORA)}
      style={{
        minHeight: TACTIL,
        borderRadius: radio.boton,
        backgroundColor: '#ece3cf',
        color: '#3a2c1f',
        boxShadow: '0 0 0 1.5px #c9a24a',
        fontWeight: 800,
        fontSize: 15,
      }}
    >
      <Trans>Open your logbook</Trans> ›
    </Button>
  );

  if (lista.length === 0) {
    return (
      <View style={{ padding: `0 ${espacio.margen}px`, gap: 12 }}>
        <EstadoVacio
          ilustracion="ancla"
          titulo={t('No voyage has arrived yet')}
          texto={t(
            'When one arrives, its bottle opens and waits for you here, on the shelf.',
          )}
          data-testid="ahorros-cumplidas-vacio"
        />
        {abrirBitacora}
      </View>
    );
  }

  return (
    <>
      <View
        data-testid="ahorros-repisa"
        style={{
          margin: `0 ${espacio.margen}px`,
          borderRadius: radio.tarjeta,
          overflow: 'hidden',
          backgroundColor: MADERA.fondo,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: 12,
            height: 96,
            padding: '0 16px 3px',
            backgroundColor: MADERA.repisa,
            borderBottom: `8px solid ${MADERA.canto}`,
            overflow: 'hidden',
          }}
        >
          {lista.slice(-8).map(c => (
            <Botella
              key={c.recuerdo.base.rid}
              aspecto={aspectoDeRecuerdo(c.recuerdo, hoy)}
              abierta
              ancho={30}
            />
          ))}
        </View>
        <View style={{ padding: '14px 16px 16px', gap: 12 }}>
          <View data-ayuda="ahorros-total">
            <Text
              style={{
                ...num,
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 30,
                color: '#f4ead6',
              }}
            >
              {format(totales.total, 'financial')}
            </Text>
            <Text
              style={{
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#d8c8aa',
              }}
            >
              {t('saved on voyages that arrived')}
            </Text>
          </View>
          {abrirBitacora}
        </View>
      </View>
      <Tarjeta relleno={0} style={{ margin: `0 ${espacio.margen}px` }}>
        {[...lista].reverse().map((c, i) => (
          <FilaCumplida
            key={c.recuerdo.base.rid}
            cumplida={c}
            primera={i === 0}
            fecha={monthUtils.format(c.llegada, 'd MMM yyyy', locale)}
            importe={format(c.importe, 'financial')}
            hoy={hoy}
          />
        ))}
      </Tarjeta>
    </>
  );
}

function FilaCumplida({
  cumplida,
  primera,
  fecha,
  importe,
  hoy,
}: {
  cumplida: Cumplida;
  primera: boolean;
  fecha: string;
  importe: string;
  hoy: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const r = cumplida.recuerdo;
  return (
    <Button
      variant="bare"
      data-testid="ahorros-cumplida"
      onPress={() =>
        void navigate(`${RUTA_BITACORA}?rid=${encodeURIComponent(r.base.rid)}`)
      }
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 12,
        padding: `12px ${espacio.margen}px`,
        borderTop: primera ? undefined : `1px solid ${color.line}`,
        borderRadius: 0,
        textAlign: 'left',
      }}
    >
      <Botella aspecto={aspectoDeRecuerdo(r, hoy)} abierta ancho={26} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontSize: 15, fontWeight: 700, color: color.fg }}>
          {[r.base.emoji, r.base.nombre].filter(Boolean).join(' ')}
        </Text>
        <Text style={{ fontSize: 12.5, color: color.fg3 }}>
          {t('Arrived on {{date}}', { date: fecha })}
        </Text>
      </View>
      <Text style={{ ...num, fontWeight: 800, color: color.fg }}>
        {importe}
      </Text>
    </Button>
  );
}
